import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Default theme values
const defaultTheme = {
  // Branding
  brand_name: "RijekaBooking",
  logo_url: null,
  favicon_url: null,
  
  // Hero Section - Background
  hero_background_url: null,
  
  // Hero Title per language
  hero_title_hr: "Luksuzni smještaji u Rijeci",
  hero_title_en: "Luxury Properties in Rijeka",
  hero_title_it: "Alloggi di Lusso a Fiume",
  hero_title_de: "Luxusunterkünfte in Rijeka",
  
  // Hero Subtitle per language
  hero_subtitle_hr: "Doživite vrhunski smještaj s dostupnošću u stvarnom vremenu, profesionalnom uslugom i trenutnom potvrdom rezervacije.",
  hero_subtitle_en: "Experience premium accommodations with real-time availability, professional service, and instant booking confirmation.",
  hero_subtitle_it: "Vivi alloggi premium con disponibilità in tempo reale, servizio professionale e conferma immediata della prenotazione.",
  hero_subtitle_de: "Erleben Sie erstklassige Unterkünfte mit Echtzeit-Verfügbarkeit, professionellem Service und sofortiger Buchungsbestätigung.",
  
  // Section Title (Why Book With Us) per language
  section_title_hr: "Zašto rezervirati kod nas",
  section_title_en: "Why Book With Us",
  section_title_it: "Perché prenotare con noi",
  section_title_de: "Warum bei uns buchen",
  
  // Section Subtitle per language
  section_subtitle_hr: "Optimizirali smo svaki aspekt vašeg iskustva rezervacije",
  section_subtitle_en: "We've optimized every aspect of your booking experience",
  section_subtitle_it: "Abbiamo ottimizzato ogni aspetto della tua esperienza di prenotazione",
  section_subtitle_de: "Wir haben jeden Aspekt Ihrer Buchungserfahrung optimiert",
  
  // Typography - separate fonts for headings and body
  font_heading: "Playfair Display",
  font_body: "Inter",
  
  // Calendar
  color_calendar_selection: "#C9A961",
  
  // Primary Colors
  color_primary: "#C9A961",
  color_primary_hover: "#B8984F",
  color_primary_dark: "#AD864C",
  color_primary_light: "#E7CA93",
  
  // Neutral Colors
  color_dark: "#35393C",
  color_dark_hover: "#2A2D2F",
  
  // Background Colors
  color_background: "#EBE9E4",
  color_background_light: "#F5F3EE",
  color_background_white: "#FFFFFF",
  
  // Border & Input Colors
  color_border: "#D3D0C7",
  color_border_light: "#E5E5E5",
  
  // Text Colors
  color_text_primary: "#35393C",
  color_text_secondary: "#57534e",
  color_text_muted: "#78716c",
  color_text_white: "#FFFFFF"
};

const ThemeContext = createContext({
  theme: defaultTheme,
  loading: true,
  refreshTheme: () => {},
  getHeroTitle: () => '',
  getHeroSubtitle: () => '',
  getSectionTitle: () => '',
  getSectionSubtitle: () => ''
});

export const useTheme = () => useContext(ThemeContext);

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(defaultTheme);
  const [loading, setLoading] = useState(true);

  const fetchTheme = async () => {
    try {
      const response = await axios.get(`${API}/theme`);
      setTheme({ ...defaultTheme, ...response.data });
    } catch (error) {
      console.error('Failed to fetch theme:', error);
      setTheme(defaultTheme);
    } finally {
      setLoading(false);
    }
  };

  const refreshTheme = () => {
    fetchTheme();
  };

  // Helper function to get hero title based on language
  const getHeroTitle = (lang) => {
    const key = `hero_title_${lang}`;
    return theme[key] || theme.hero_title_en || defaultTheme.hero_title_en;
  };

  // Helper function to get hero subtitle based on language
  const getHeroSubtitle = (lang) => {
    const key = `hero_subtitle_${lang}`;
    return theme[key] || theme.hero_subtitle_en || defaultTheme.hero_subtitle_en;
  };

  // Helper function to get section title based on language
  const getSectionTitle = (lang) => {
    const key = `section_title_${lang}`;
    return theme[key] || theme.section_title_en || defaultTheme.section_title_en;
  };

  // Helper function to get section subtitle based on language
  const getSectionSubtitle = (lang) => {
    const key = `section_subtitle_${lang}`;
    return theme[key] || theme.section_subtitle_en || defaultTheme.section_subtitle_en;
  };

  useEffect(() => {
    fetchTheme();
  }, []);

  // Apply CSS variables whenever theme changes
  useEffect(() => {
    const root = document.documentElement;

    // Convert hex (#RRGGBB) to "H S% L%" string used by shadcn/Tailwind tokens.
    const hexToHslTriplet = (hex) => {
      if (!hex || typeof hex !== 'string') return null;
      let h = hex.replace('#', '');
      if (h.length === 3) h = h.split('').map(c => c + c).join('');
      if (h.length !== 6) return null;
      const r = parseInt(h.slice(0, 2), 16) / 255;
      const g = parseInt(h.slice(2, 4), 16) / 255;
      const b = parseInt(h.slice(4, 6), 16) / 255;
      const max = Math.max(r, g, b), min = Math.min(r, g, b);
      let hue = 0, sat = 0;
      const l = (max + min) / 2;
      if (max !== min) {
        const d = max - min;
        sat = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
          case r: hue = (g - b) / d + (g < b ? 6 : 0); break;
          case g: hue = (b - r) / d + 2; break;
          default: hue = (r - g) / d + 4;
        }
        hue *= 60;
      }
      return `${Math.round(hue)} ${Math.round(sat * 100)}% ${Math.round(l * 100)}%`;
    };

    // Set CSS variables
    root.style.setProperty('--color-primary', theme.color_primary);
    root.style.setProperty('--color-primary-hover', theme.color_primary_hover);
    root.style.setProperty('--color-primary-dark', theme.color_primary_dark);
    root.style.setProperty('--color-primary-light', theme.color_primary_light);

    // Sync shadcn HSL tokens so default `<Button>` and other Tailwind utilities
    // (bg-primary, text-primary, etc.) follow the active brand color.
    const primaryHsl = hexToHslTriplet(theme.color_primary);
    if (primaryHsl) {
      root.style.setProperty('--primary', primaryHsl);
      root.style.setProperty('--ring', primaryHsl);
    }

    root.style.setProperty('--color-dark', theme.color_dark);
    root.style.setProperty('--color-dark-hover', theme.color_dark_hover);
    root.style.setProperty('--color-background', theme.color_background);
    root.style.setProperty('--color-background-light', theme.color_background_light);
    root.style.setProperty('--color-background-white', theme.color_background_white);
    root.style.setProperty('--color-border', theme.color_border);
    root.style.setProperty('--color-border-light', theme.color_border_light);
    root.style.setProperty('--color-text-primary', theme.color_text_primary);
    root.style.setProperty('--color-text-secondary', theme.color_text_secondary);
    root.style.setProperty('--color-text-muted', theme.color_text_muted);
    root.style.setProperty('--color-text-white', theme.color_text_white);
    
    // Calendar selection color
    root.style.setProperty('--color-calendar-selection', theme.color_calendar_selection || theme.color_primary);
    
    // Load Google Fonts dynamically - separate heading and body fonts
    const fontsToLoad = [];
    if (theme.font_heading) fontsToLoad.push(theme.font_heading);
    if (theme.font_body && theme.font_body !== theme.font_heading) fontsToLoad.push(theme.font_body);
    
    if (fontsToLoad.length > 0) {
      const fontFamilies = fontsToLoad.map(f => f.replace(/ /g, '+')).join('&family=');
      const fontLink = document.getElementById('theme-font');
      const fontUrl = `https://fonts.googleapis.com/css2?family=${fontFamilies}:wght@300;400;500;600;700&display=swap`;
      
      if (fontLink) {
        fontLink.href = fontUrl;
      } else {
        const link = document.createElement('link');
        link.id = 'theme-font';
        link.rel = 'stylesheet';
        link.href = fontUrl;
        document.head.appendChild(link);
      }
    }
    
    // Set font CSS variables
    root.style.setProperty('--font-heading', theme.font_heading ? `"${theme.font_heading}", serif` : '"Playfair Display", serif');
    root.style.setProperty('--font-heading-weight', theme.font_heading_weight || '600');
    root.style.setProperty('--font-body', theme.font_body ? `"${theme.font_body}", sans-serif` : '"Inter", sans-serif');
    
    // Set border radius CSS variables based on theme setting
    const borderRadiusValues = {
      none: { sm: '0px', md: '0px', lg: '0px', xl: '0px', full: '0px' },
      small: { sm: '4px', md: '6px', lg: '8px', xl: '12px', full: '9999px' },
      medium: { sm: '6px', md: '8px', lg: '12px', xl: '16px', full: '9999px' },
      large: { sm: '10px', md: '14px', lg: '20px', xl: '28px', full: '9999px' }
    };
    const radius = borderRadiusValues[theme.border_radius] || borderRadiusValues.medium;
    root.style.setProperty('--border-radius-sm', radius.sm);
    root.style.setProperty('--border-radius-md', radius.md);
    root.style.setProperty('--border-radius-lg', radius.lg);
    root.style.setProperty('--border-radius-xl', radius.xl);
    root.style.setProperty('--border-radius-full', radius.full);
    
    // Set favicon
    if (theme.favicon_url) {
      const favicon = document.querySelector('link[rel="icon"]');
      if (favicon) {
        favicon.href = theme.favicon_url;
      }
    }
    
    // Set page title
    if (theme.brand_name) {
      document.title = theme.brand_name;
    }
    
  }, [theme]);

  return (
    <ThemeContext.Provider value={{ theme, loading, refreshTheme, getHeroTitle, getHeroSubtitle, getSectionTitle, getSectionSubtitle }}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeContext;
